import React from 'react';
import type OnlineApplication from '@this/src/domain/online_application/online_application';
import { colors } from '@this/shared/ui/theme/tokens/primitive-tokens/colors';
import { getOptionPlanName } from '@this/domain/option_plan';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio/radio';
import { OnlineApplicationBilling } from '@this/src/domain/online_application/online_application_billing';
import { adminOnlineApplicationStyles } from '../../admin/online_applications/shared/styles';
import { onlineApplicationStyles } from './styles';

interface OnlineApplicationViewProps {
  application: OnlineApplication;
  contactName?: string;
  contactEmail?: string;
  contactTel?: string;
  startDesiredMonth?: string;
  submitterCompanyName?: string;
  submitterDepartment?: string;
  submitterName?: string;
  submitterEmail?: string;
  bankName?: string;
  branchName?: string;
  accountType?: string;
  accountHolderName?: string;
  accountNumber?: string;
  customStartDesiredMonth?: React.ReactNode;
  customCompanyFields?: React.ReactNode;
  customBankAccountFields?: React.ReactNode;
  customSubmitterFields?: React.ReactNode;
  isAdmin?: boolean;
}

export const OnlineApplicationView: React.FC<OnlineApplicationViewProps> = ({
  application,
  contactName,
  contactEmail,
  contactTel,
  startDesiredMonth,
  submitterCompanyName,
  submitterDepartment,
  submitterName,
  submitterEmail,
  bankName,
  branchName,
  accountType,
  accountHolderName,
  accountNumber,
  customStartDesiredMonth,
  customCompanyFields,
  customBankAccountFields,
  customSubmitterFields,
  isAdmin = false
}) => {
  const styles = isAdmin ? adminOnlineApplicationStyles : onlineApplicationStyles;

  // Admin側専用フィールド
  function renderAdminOnlyFields() {
    return (
      <>
        <h3 style={styles.heading.base}>その他</h3>
        <div style={styles.section.base}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={styles.field.base}>
              <span style={styles.label.base}>月間GMV 見込み金額（税抜）</span>
              <span style={styles.value.base}>{application.potentialGmv || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>Salesforce URL（企業情報ページ）</span>
              <span style={styles.value.base}>{application.salesforceUrl || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>対象企業の実在性・実態を疑うべき事情</span>
              <span style={styles.value.base}>{application.existence || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>与信を下げると思慮される情報</span>
              <span style={styles.value.base}>{application.creditDownComment || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>その他取引を否定すべき事情</span>
              <span style={styles.value.base}>{application.blockingFactorComment || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>それでもなお取引すべきと思慮する根拠</span>
              <span style={styles.value.base}>{application.overrideJustificationComment || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>関連するslack URL</span>
              <span style={styles.value.base}>{application.slackUrl || '-'}</span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>その他コメント</span>
              <span style={styles.value.base}>{application.otherComment || '-'}</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div>
      <h3 style={styles.heading.base}>システム利用および立替払に関するお申込書</h3>

      {/* 開始希望月 */}
      {customStartDesiredMonth || (
        <>
          <h3 style={styles.heading.base}>開始希望月</h3>
          <div style={styles.section.base}>
            <span style={styles.value.base}>
              {(() => {
                const month = startDesiredMonth || application.startDesiredMonth;
                if (!month) return '-';
                const dateMatch = month.match(/^(\d{4})-(\d{2})/);
                if (dateMatch) {
                  return `${dateMatch[1]}/${dateMatch[2]}`;
                }
                return month;
              })()}
            </span>
          </div>
        </>
      )}

      {/* 法人情報 */}
      {(() => {
        // 1件目の法人情報（confirmにも対応）
        const firstCompany = {
          nameKana: application.companyNameKana || application.companies?.[0]?.name_kana,
          name: application.companyName || application.companies?.[0]?.name,
          postalCode: application.companyPostalCode || application.companies?.[0]?.postal_code,
          address: application.companyAddress || application.companies?.[0]?.address,
          tel: application.companyTel || application.companies?.[0]?.tel,
          contactNameKana: application.contactNameKana || application.companies?.[0]?.contact_name_kana,
          contactName: contactName || application.contactName || application.companies?.[0]?.contact_name,
          contactEmail: contactEmail || application.contactEmail || application.companies?.[0]?.contact_email,
          contactTel: contactTel || application.contactTel || application.companies?.[0]?.contact_tel
        };

        // 追加法人のリスト
        const companiesArray = application.companies ? Array.from(application.companies) : [];
        const additionalCompaniesArray = application.additionalCompanies
          ? Array.from(application.additionalCompanies)
          : null;
        const additionalCompanies =
          additionalCompaniesArray && additionalCompaniesArray.length > 0
            ? additionalCompaniesArray
            : companiesArray.slice(1) || [];

        // 全ての法人を統合（1件目 + 追加分）
        const allCompanies = [firstCompany, ...additionalCompanies];

        return (
          <>
            {allCompanies.map((company, index) => (
              <div key={index}>
                <h3 style={styles.heading.base}>{index === 0 ? '法人情報' : `法人情報${index + 1}`}</h3>
                <div style={styles.section.base}>
                  <div style={styles.grid.single}>
                    <div style={styles.field.base}>
                      <span style={styles.label.base}>法人名 カナ</span>
                      <span style={styles.value.base}>{company.nameKana || company.name_kana || '-'}</span>
                    </div>
                    <div style={styles.field.base}>
                      <span style={styles.label.base}>法人名 漢字</span>
                      <span style={styles.value.base}>{company.name || '-'}</span>
                    </div>
                    <div style={styles.field.base}>
                      <span style={styles.label.base}>法人 郵便番号</span>
                      <span style={styles.value.base}>{company.postalCode || company.postal_code || '-'}</span>
                    </div>
                    <div style={styles.field.base}>
                      <span style={styles.label.base}>法人 住所</span>
                      <span style={styles.value.base}>{company.address || '-'}</span>
                    </div>
                    <div style={styles.field.base}>
                      <span style={styles.label.base}>法人 電話番号</span>
                      <span style={styles.value.base}>{company.tel || '-'}</span>
                    </div>
                    {index === 0 && customCompanyFields ? (
                      customCompanyFields
                    ) : (
                      <>
                        <div style={styles.field.base}>
                          <span style={styles.label.base}>担当者名 カナ</span>
                          <span style={styles.value.base}>
                            {company.contactNameKana || company.contact_name_kana || '-'}
                          </span>
                        </div>
                        <div style={styles.field.base}>
                          <span style={styles.label.base}>担当者名 漢字</span>
                          <span style={styles.value.base}>
                            {company.contactName || company.contact_name || '-'}
                          </span>
                        </div>
                        <div style={styles.field.base}>
                          <span style={styles.label.base}>担当者 メールアドレス</span>
                          <span style={styles.value.base}>
                            {company.contactEmail || company.contact_email || '-'}
                          </span>
                        </div>
                        <div style={styles.field.base}>
                          <span style={styles.label.base}>担当者 電話番号</span>
                          <span style={styles.value.base}>{company.contactTel || company.contact_tel || '-'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        );
      })()}

      {/* 申込者情報 */}
      {(customSubmitterFields ||
        submitterCompanyName ||
        submitterDepartment ||
        submitterName ||
        submitterEmail) && (
        <>
          <h3 style={styles.heading.base}>申込者情報</h3>
          <div style={styles.section.base}>
            {customSubmitterFields || (
              <div style={styles.grid.single}>
                {submitterCompanyName && (
                  <div style={styles.field.base}>
                    <span style={styles.label.base}>会社名</span>
                    <span style={styles.value.base}>{submitterCompanyName}</span>
                  </div>
                )}
                {submitterDepartment && (
                  <div style={styles.field.base}>
                    <span style={styles.label.base}>所属部署名</span>
                    <span style={styles.value.base}>{submitterDepartment}</span>
                  </div>
                )}
                {submitterName && (
                  <div style={styles.field.base}>
                    <span style={styles.label.base}>氏名</span>
                    <span style={styles.value.base}>{submitterName}</span>
                  </div>
                )}
                {submitterEmail && (
                  <div style={styles.field.base}>
                    <span style={styles.label.base}>メールアドレス</span>
                    <span style={styles.value.base}>{submitterEmail}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* 請求先情報 */}
      <h3 style={styles.heading.base}>請求先情報</h3>
      <div style={styles.section.base}>
        <div style={{ marginBottom: '24px' }}>
          {/* Material-UIのRadioボタンはdisabled時に自動的に薄くなるため、style jsxでCSSセレクタを使って上書きする必要がある */}
          <style jsx>{`
            .billing-radio-group .MuiFormControlLabel-root.Mui-disabled {
              opacity: 1 !important;
            }
            .billing-radio-group .MuiFormControlLabel-label.Mui-disabled {
              color: ${colors.gray[900]} !important;
            }
          `}</style>
          <div className="billing-radio-group">
            <RadioGroup value={application.isBillingSameAsCompany ? 'same' : 'different'} layout="horizontal">
              <Radio value="same" disabled>
                法人情報と同じ
              </Radio>
              <Radio value="different" disabled>
                法人情報と異なる
              </Radio>
            </RadioGroup>
          </div>
        </div>

        {(() => {
          return (
            !application.isBillingSameAsCompany && (
              <div style={styles.grid.single}>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>請求先 郵便番号</span>
                  <span style={styles.value.base}>
                    {application.billingPostalCode || application.billing?.postalAddress || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>請求先 住所</span>
                  <span style={styles.value.base}>
                    {application.billingAddress || application.billing?.address || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>請求先 電話番号</span>
                  <span style={styles.value.base}>
                    {application.billingTel || application.billing?.tel || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>請求先 部署・役職</span>
                  <span style={styles.value.base}>
                    {application.billingDepartment || application.billing?.department || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>担当者名 カナ</span>
                  <span style={styles.value.base}>
                    {application.billingContactNameKana || application.billing?.contactNameKana || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>担当者名 漢字</span>
                  <span style={styles.value.base}>
                    {application.billingContactName || application.billing?.contactName || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>担当者 メールアドレス</span>
                  <span style={styles.value.base}>
                    {application.billingContactEmail || application.billing?.contactEmail || '-'}
                  </span>
                </div>
                <div style={styles.field.base}>
                  <span style={styles.label.base}>担当者 電話番号</span>
                  <span style={styles.value.base}>
                    {application.billingContactTel || application.billing?.contactTel || '-'}
                  </span>
                </div>
              </div>
            )
          );
        })()}
      </div>

      {/* 銀行情報 */}
      <h3 style={styles.heading.base}>返金口座</h3>
      <div style={styles.section.base}>
        <div style={{ marginBottom: '14px' }}>
          <h4
            style={{
              ...styles.heading?.sub,
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}
          >
            支払条件
          </h4>
          <p style={{ margin: 0, color: colors.gray[700], lineHeight: '1.6' }}>
            毎月末日を締め日として、翌月末日までにトランスファーデータ指定の銀行口座に振り込むことで支払う。
          </p>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <h4
            style={{
              ...styles.heading?.sub,
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}
          >
            請求書送付
          </h4>
          <p style={{ margin: 0, color: colors.gray[700], lineHeight: '1.6' }}>
            請求書の送付については、弊社トランスファーデータより、締め日から5営業日以内に電子的手段にて貴社へお送りいたします。
            <br />
            ただし、請求書発行期日変更オプションをご利用の場合は、当該オプションの規定に基づき発行いたします。
          </p>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <h4
            style={{
              ...styles.heading?.sub,
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}
          >
            返金口座
          </h4>
          <p style={{ margin: 0, color: colors.gray[700], lineHeight: '1.6' }}>
            弊社から貴社への旅行代金の返金が発生する場合がございますので、返金用口座をご入力ください
          </p>
        </div>

        {customBankAccountFields || (
          <div style={styles.grid.single}>
            <div style={styles.field.base}>
              <span style={styles.label.base}>銀行名</span>
              <span style={styles.value.base}>
                {bankName || application.bankName || application.billing?.bankName || '-'}
              </span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>支店名</span>
              <span style={styles.value.base}>
                {branchName || application.branchName || application.billing?.branchName || '-'}
              </span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>口座種別</span>
              <span style={styles.value.base}>
                {(() => {
                  const typeValue = accountType || application.accountType || application.billing?.accountType;
                  if (!typeValue) return '-';
                  // 数値文字列として渡された場合の処理
                  if (typeValue === '1' || String(typeValue) === '1') return '普通預金';
                  if (typeValue === '2' || String(typeValue) === '2') return '当座預金';
                  // 文字列として渡された場合の処理
                  const tempBilling = new OnlineApplicationBilling({ account_type: typeValue });
                  return tempBilling.accountTypeLabel;
                })()}
              </span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>口座名義(カナ)</span>
              <span style={styles.value.base}>
                {accountHolderName ||
                  application.accountHolderName ||
                  application.billing?.accountHolderName ||
                  '-'}
              </span>
            </div>
            <div style={styles.field.base}>
              <span style={styles.label.base}>口座番号</span>
              <span style={styles.value.base}>
                {accountNumber || application.accountNumber || application.billing?.accountNumber || '-'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* プラン・オプション */}
      <h3 style={styles.heading.base}>お申込プラン</h3>
      <div style={styles.section.base}>
        <div style={{ width: '90%' }}>
          {/* 初期費用 - initialCastsがある場合はそちらを優先 */}
          {application.initialCosts && application.initialCosts.length > 0 ? (
            application.initialCosts.map((initialCost, index) => (
              <div
                key={index}
                style={{
                  borderTop: index === 0 ? `1px solid ${colors.gray[200]}` : 'none',
                  borderBottom: `1px solid ${colors.gray[200]}`,
                  padding: '12px  16px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{initialCost.name || '初期費用'}</span>
                  <span>{(initialCost.price || 0).toLocaleString()} 円</span>
                </div>
              </div>
            ))
          ) : (
            // 従来の形式（後方互換性のため）
            <div
              style={{
                borderTop: `1px solid ${colors.gray[200]}`,
                borderBottom: `1px solid ${colors.gray[200]}`,
                padding: '12px 16px 0'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{application.initialCostLabel || '初期費用'}</span>
                <span>{application.initialCostPrice?.toLocaleString() || '0'} 円</span>
              </div>
            </div>
          )}

          {/* 基本プラン - plansがある場合はそちらを優先 */}
          {application.plans && application.plans.length > 0 ? (
            application.plans.map((plan, index) => (
              <div
                key={index}
                style={{
                  borderBottom: `1px solid ${colors.gray[200]}`,
                  padding: '12px 16px'
                }}
              >
                <div style={{ marginBottom: '4px' }}>
                  <span>{plan.name || 'AI Travel 出張管理システム サービス利用基本料金'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>プラン名：{plan.planMasterName || ''}</span>
                  <span>{(plan.price || 0).toLocaleString()} 円／月</span>
                </div>
              </div>
            ))
          ) : (
            // 従来の形式（後方互換性のため）
            <div
              style={{
                borderBottom: `1px solid ${colors.gray[200]}`,
                padding: '12px 16px'
              }}
            >
              {/* <div style={{ marginBottom: '8px' }}> */}
              <div style={{ marginBottom: '4px' }}>
                <span>{application.basicPlanLabel || 'AI Travel 出張管理システム サービス利用基本料金'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  プラン名：
                  {(() => {
                    // 初期費用から取得を試みる
                    if (application.initialCosts && application.initialCosts.length > 0) {
                      const planId = application.initialCosts[0].planId;
                      if (planId === 1) return 'ベーシック';
                      if (planId === 2) return 'エンタープライズ';
                    }
                    return '';
                  })()}
                </span>
                <span>
                  {application.basicPlanPrice !== null && application.basicPlanPrice !== undefined
                    ? application.basicPlanPrice.toLocaleString()
                    : '0'}{' '}
                  円／月
                </span>
              </div>
            </div>
          )}

          {/* サポート手数料 */}
          {application.showSupportFee && (
            <div
              style={{
                borderBottom: `1px solid ${colors.gray[200]}`,
                padding: '12px 16px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span>{application.supportFeeName || 'サポート手数料'}</span>
                <span style={{ fontSize: '12px', textAlign: 'right' }}>
                  <a
                    href="https://aitravel.cloud/tos/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0066cc', textDecoration: 'underline' }}
                  >
                    利用約款
                  </a>
                  の定めに従うものとする。
                </span>
              </div>
            </div>
          )}

          {/* オプション */}
          {application.options && application.options.length > 0 && (
            <>
              {application.options.map((option, index) => (
                <div
                  key={index}
                  style={{
                    borderBottom: `1px solid ${colors.gray[200]}`,
                    padding: '12px 16px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{getOptionPlanName(option.option_plan_id)}</span>
                    <span>{option.price?.toLocaleString() || '0'} 円／月</span>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* 合計 */}
          <div
            style={{
              ...styles.total.container,
              backgroundColor: colors.gray[50],
              borderTop: 'none',
              borderBottom: 'none'
            }}
          >
            <span>月額システム利用料金　合計</span>
            <span>
              {(() => {
                // plansがある場合はそちらを優先
                const planTotal =
                  application.plans && application.plans.length > 0
                    ? application.plans.reduce((sum, plan) => sum + (plan.price || 0), 0)
                    : // 従来の形式
                    application.basicPlanPrice !== null && application.basicPlanPrice !== undefined
                    ? application.basicPlanPrice
                    : 0;

                const optionTotal = application.options
                  ? application.options.reduce((sum, opt) => sum + (opt.price || 0), 0)
                  : 0;

                const total = planTotal + optionTotal;
                return total.toLocaleString();
              })()}{' '}
              円／月
            </span>
          </div>

          {/* 請求書払い */}
          {application.invoicePaymentRequested !== undefined && (
            <div
              style={{
                borderTop: `1px solid ${colors.gray[200]}`,
                borderBottom: `1px solid ${colors.gray[200]}`,
                padding: '12px 16px'
              }}
            >
              <span>
                {application.invoicePaymentRequested ? '請求書払いを希望する' : '請求書払いを希望しない'}
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Admin側専用：その他情報 */}
      {isAdmin && renderAdminOnlyFields()}
    </div>
  );
};
